import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, Upload } from "lucide-react";
import { cn } from "@/lib/utils";
import { ManagedImage } from "@/components/desk/SortableImageUploader";
export type { ManagedImage };

interface SingleImageUploaderProps {
  title: string;
  description?: string;
  image: ManagedImage | null | undefined;
  uploadPath: string;
  deletePath: (id: number) => string;
  kind: "profile" | "cover";
  onChange?: (image: ManagedImage | null) => void;
}

export function SingleImageUploader({
  title,
  description,
  image,
  uploadPath,
  deletePath,
  kind,
  onChange,
}: SingleImageUploaderProps) {
  const [current, setCurrent] = useState<ManagedImage | null>(image ?? null);
  const [uploading, setUploading] = useState(false);
  const csrfToken = useMemo(
    () => (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content,
    []
  );

  useEffect(() => {
    setCurrent(image ?? null);
  }, [image]);

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("image[file]", file);
      formData.append("image[kind]", kind);

      const response = await fetch(uploadPath, {
        method: "POST",
        headers: {
          Accept: "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
        body: formData,
      });

      if (!response.ok) return;
      const payload = await response.json();
      setCurrent(payload.data);
      onChange?.(payload.data);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!current) return;

    const response = await fetch(deletePath(current.id), {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      },
    });

    if (response.ok) {
      setCurrent(null);
      onChange?.(null);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">
        <label
          className={cn(
            "flex h-32 cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-muted-foreground/30 bg-muted/40 transition hover:border-primary/70 hover:bg-muted/60",
            uploading && "opacity-70"
          )}
        >
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => handleFile(event.target.files?.[0] || null)}
          />
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {uploading ? "يتم رفع الصورة..." : "اسحب الصورة هنا أو اضغط للاختيار"}
          </div>
          <p className="text-xs text-muted-foreground">ملف صورة واحد</p>
        </label>

        {current ? (
          <div className="flex flex-col gap-3 rounded-lg border bg-background p-3 shadow-sm">
            <div className="relative h-48 overflow-hidden rounded-md bg-muted">
              <img
                src={current.url}
                alt={current.filename}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div>
                <p className="font-medium text-foreground">{current.filename}</p>
                <p>
                  {(current.byte_size / 1024 / 1024).toFixed(1)} MB · {current.content_type}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:text-destructive"
                onClick={handleDelete}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-muted-foreground/30 bg-muted/40 p-6 text-center text-sm text-muted-foreground">
            لم يتم تعيين صورة بعد
          </div>
        )}
      </CardContent>
    </Card>
  );
}
